<template>
  <BuilderWrapper
    title="Bubble Parameters"
    helpful-tip="Customise the bubble visualiation"
  >
    <LabelledInput
      label="Bubble force type"
      helpful-tip="How the bubbles interact with each other"
    >
      <b-radio
        v-model="forceType"
        :value="COLLISION"
      >
        Collision force <HelpfulTooltip text="Recommended option. Easy to configure but may perform poorly on older browsers" />
      </b-radio>
      <b-radio
        v-model="forceType"
        :value="MANY_BODY"
      >
        Many body force <HelpfulTooltip text="Better performance but harder to configure" />
      </b-radio>
      <b-radio
        v-model="forceType"
        :value="COMBO"
      >
        Combo <HelpfulTooltip text="Use collision force for all tabs except for root which uses many body force" />
      </b-radio>
    </LabelledInput>

    <template v-for="param of params">
      <RepulsionStrengthSlider
        v-if="param.name==='mbStrength'"
        :key="`bubble-repulsion-${param.name}`"
        :param-object="param"
      />
      <BubbleMinSlider
        v-else-if="param.name==='bubbleMin'"
        :key="`bubble-min-${param.name}`"
        :param-object="param"
      />
      <BubbleParamSlider
        v-else
        :key="`bubble-param-${param.name}`"
        :param-object="param"
      />
    </template>

    <LabelledInput>
      <b-checkbox v-model="showZeroValueBubbles">
        Show Zero Value Bubbles <HelpfulTooltip text="Show Bubbles that have a Total value of zero" />
      </b-checkbox>
    </LabelledInput>
  </BuilderWrapper>
</template>

<script lang="ts">

import Vue from 'vue'
import Component from 'vue-class-component'

import HelpfulTooltip from '@/components/builder/UI/HelpfulTooltip.vue'
import BuilderWrapper from '@/components/builder/UI/BuilderWrapper.vue'
import LabelledInput from '@/components/builder/UI/LabelledInput.vue'
import BubbleParamSlider from '@/components/builder/BubbleParams/BubbleParamSlider.vue'
import BubbleMinSlider from '@/components/builder/BubbleParams/BubbleMinSlider.vue'
import RepulsionStrengthSlider from '@/components/builder/BubbleParams/RepulsionStrengthSlider.vue'
import { BubbleForceTypes } from '@/types'

const components = {
  BuilderWrapper,
  LabelledInput,
  BubbleParamSlider,
  RepulsionStrengthSlider,
  BubbleMinSlider,
  HelpfulTooltip,
}

@Component({ components })
export default class BubbleParams extends Vue {
  COLLISION = BubbleForceTypes.COLLISION
  COMBO = BubbleForceTypes.COMBO
  MANY_BODY = BubbleForceTypes.MANY_BODY
  commonParams = [
    {
      name: 'bubbleMax',
      label: 'Maximum bubble size',
      min: 0,
      max: 1000,
      step: 1,
      description: 'Largest bubble size to display',
    },
    {
      name: 'bubbleMin',
      label: 'Minimum bubble size',
      min: 0,
      max: 1000,
      step: 1,
      description: 'Smallest bubble size to display',
    },
    {
      name: 'sideBubblePositionX',
      label: 'Horizontal Side Bubble Position',
      min: -1500,
      max: 1500,
      step: 10,
      description: 'How far left or right the bubbles are located when clicking the lower tabs',
    },
    {
      name: 'sideBubblePositionY',
      label: 'Vertical Side Bubble Position',
      min: -1500,
      max: 1500,
      step: 10,
      description: 'How far up or down the bubbles are located when clicking the lower tabs',
    },
  ]

  collisionParams = [
    {
      name: 'spacing',
      label: 'Spacing',
      min: 0,
      max: 50,
      step: 1,
      description: 'The amount of space between each bubble',
    },
  ]

  manyBodyParams = [
    {
      name: 'mbStrength',
      label: 'Repulsion Strength',
      min: 0,
      max: 100,
      step: 0.1,
      description: 'Strength of the force that pushes the bubbles apart',
    },
  ]

  mounted () {
    this.$typedStore.public.display.setShowHandles(true)
  }

  destroyed () {
    this.$typedStore.public.display.setShowHandles(false)
  }

  get bubbleParams () {
    return this.$typedStore.primary.app.config.bubbleParams
  }

  get params () {
    if (this.forceType === BubbleForceTypes.MANY_BODY) {
      return this.manyBodyParams.concat(this.commonParams)
    } else if (this.forceType === BubbleForceTypes.COMBO) {
      return this.manyBodyParams.concat(this.collisionParams, this.commonParams)
    } else { // Bubble force type collision
      return this.collisionParams.concat(this.commonParams)
    }
  }

  get forceType () {
    return this.$typedStore.primary.app.config.bubbleParams.forceType || BubbleForceTypes.COLLISION
  }

  set forceType (newValue) {
    this.$typedStore.primary.app.updateConfig({
      configPath: ['bubbleParams', 'forceType'],
      value: newValue,
    })
  }

  get showZeroValueBubbles () {
    return this.bubbleParams.showZeroValueBubbles
  }

  set showZeroValueBubbles (newValue) {
    this.$typedStore.primary.app.updateConfig({
      configPath: ['bubbleParams', 'showZeroValueBubbles'],
      value: newValue,
    })
  }
}

</script>
